import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
  Typography,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Button,
  Divider,
  Chip,
  LinearProgress,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import ChallengesPage from './components/ChallengesPage';
import InstallPrompt from './components/InstallPrompt';
import { setUnauthorizedHandler } from './api/challengesApi';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#0ea5e9' },
    background: {
      default: '#0f172a',
      paper: 'rgba(15, 23, 42, 0.8)'
    },
    text: { primary: '#e2e8f0' }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { minHeight: 44, borderRadius: 10 }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: { minWidth: 44, minHeight: 44 }
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: { minWidth: 0, flex: 1 }
      }
    }
  }
});

const AUTH_URL = 'https://challengeurselfauth-production.up.railway.app/api/auth/google';

const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const s = localStorage.getItem('auth_user');
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setUser(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token || isTokenExpired(token)) {
      if (token) logout();
      else setUser(null);
    }
  }, [logout]);

  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(() => {});
  }, [logout]);

  const login = async (credentialResponse) => {
    try {
      const res = await fetch(AUTH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: credentialResponse.credential })
      });
      if (!res.ok) return;
      const data = await res.json();
      localStorage.setItem('auth_token', data.accessToken);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      setUser(data.user);
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function AppHeader({ onAvatarClick }) {
  const { user } = useAuth();

  return (
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        bgcolor: 'rgba(13, 20, 38, 0.96)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        px: 2,
        zIndex: 50,
      }}
    >
      <Box
        sx={{
          height: 52,
          maxWidth: 640,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '9px',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0ea5e9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 10px rgba(14,165,233,0.45)',
            }}
          >
            <BoltIcon sx={{ fontSize: 17, color: '#fff' }} />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ letterSpacing: '-0.25px', lineHeight: 1, fontSize: '0.95rem' }}
          >
            Challenge Yourself
          </Typography>
        </Box>

        {user && (
          <Avatar
            onClick={onAvatarClick}
            sx={{
              width: 32,
              height: 32,
              bgcolor: '#0ea5e9',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer',
              border: '2px solid rgba(14,165,233,0.35)',
              transition: 'transform 0.12s, opacity 0.12s',
              userSelect: 'none',
              '&:active': { transform: 'scale(0.92)', opacity: 0.8 },
            }}
          >
            {user.email?.[0]?.toUpperCase() || '?'}
          </Avatar>
        )}
      </Box>
    </Box>
  );
}

function ProgressBarStrip({ completed, total }) {
  const pct = total > 0 ? (completed / total) * 100 : 0;
  return (
    <Box
      sx={{
        flexShrink: 0,
        bgcolor: 'rgba(13, 20, 38, 0.96)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        px: 2,
        pt: 0.75,
        pb: 1,
      }}
    >
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.4)', fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Progress
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.4)', fontSize: '0.65rem' }}>
            {completed} / {total} done
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 3,
            borderRadius: 2,
            bgcolor: 'rgba(226,232,240,0.08)',
            '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: '#0ea5e9' }
          }}
        />
      </Box>
    </Box>
  );
}

function AppFooterNav({ tab, onTabChange }) {
  return (
    <Box
      component="nav"
      sx={{
        flexShrink: 0,
        bgcolor: 'rgba(13, 20, 38, 0.96)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        zIndex: 50,
      }}
    >
      <BottomNavigation
        value={tab}
        onChange={(_, val) => onTabChange(val)}
        sx={{
          bgcolor: 'transparent',
          height: 48,
          maxWidth: 640,
          mx: 'auto',
        }}
      >
        <BottomNavigationAction
          label="Challenges"
          value="challenges"
          icon={<HomeRoundedIcon sx={{ fontSize: 22 }} />}
          sx={{
            color: 'rgba(226,232,240,0.38)',
            paddingBottom: 0,
            '&.Mui-selected': { color: '#0ea5e9' },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.62rem',
              mt: '1px',
              '&.Mui-selected': { fontSize: '0.62rem' },
            },
          }}
        />
        <BottomNavigationAction
          label="Profile"
          value="profile"
          icon={<PersonRoundedIcon sx={{ fontSize: 22 }} />}
          sx={{
            color: 'rgba(226,232,240,0.38)',
            paddingBottom: 0,
            '&.Mui-selected': { color: '#0ea5e9' },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.62rem',
              mt: '1px',
              '&.Mui-selected': { fontSize: '0.62rem' },
            },
          }}
        />
      </BottomNavigation>
    </Box>
  );
}

function ProfileView() {
  const { user, logout } = useAuth();

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?';

  return (
    <Box sx={{ p: 2.5, maxWidth: 640, mx: 'auto', pb: 4 }}>
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ mb: 2.5, letterSpacing: '-0.2px' }}
      >
        Profile
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2.5,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.07)',
          mb: 3,
        }}
      >
        <Avatar
          sx={{
            width: 52,
            height: 52,
            bgcolor: '#0ea5e9',
            fontSize: '1.25rem',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          {user?.name && (
            <Typography variant="body1" fontWeight={600} noWrap sx={{ mb: 0.25 }}>
              {user.name}
            </Typography>
          )}
          <Typography
            variant="body2"
            noWrap
            sx={{ color: 'rgba(226,232,240,0.55)' }}
          >
            {user?.email}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 3 }} />

      <Button
        variant="outlined"
        fullWidth
        onClick={logout}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderColor: 'rgba(248,113,113,0.28)',
          color: '#f87171',
          py: 1.5,
          fontSize: '0.95rem',
          '&:hover': {
            borderColor: '#f87171',
            bgcolor: 'rgba(248,113,113,0.06)',
          },
          '&:active': { bgcolor: 'rgba(248,113,113,0.12)' },
        }}
      >
        Sign out
      </Button>
    </Box>
  );
}

const ACCOUNTABILITY_OPTIONS = [
  'Pay $50',
  'Share challenge on social media',
  'Get humiliated by AI',
];

function LoginView() {
  const { login } = useAuth();
  const [challengeText, setChallengeText] = useState('');
  const [accountability, setAccountability] = useState(['Get humiliated by AI']);

  const toggleChip = (option) => {
    setAccountability((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const handleLogin = (credentialResponse) => {
    if (challengeText.trim()) {
      localStorage.setItem(
        'pending_challenge',
        JSON.stringify({ text: challengeText, accountability })
      );
    }
    login(credentialResponse);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100%',
        px: 3,
        pt: 4,
        pb: 5,
      }}
    >
      {/* Compact hero */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 3.5,
        }}
      >
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: '18px',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0ea5e9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: '0 8px 32px rgba(14,165,233,0.4)',
          }}
        >
          <BoltIcon sx={{ fontSize: 38, color: '#fff' }} />
        </Box>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ letterSpacing: '-0.4px', mb: 0.5 }}
        >
          Challenge yourself
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'rgba(226,232,240,0.5)', textAlign: 'center' }}
        >
          Set challenges. Stay accountable.
        </Typography>
      </Box>

      {/* Interactive form — explore before signing in */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 380,
          p: 2.5,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.07)',
          mb: 3,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#0ea5e9',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.62rem',
            mb: 1,
          }}
        >
          Your first challenge
        </Typography>
        <TextField
          placeholder="What will you accomplish?"
          value={challengeText}
          onChange={(e) => setChallengeText(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              fontSize: '0.92rem',
              bgcolor: 'rgba(255,255,255,0.03)',
            },
          }}
          inputProps={{ enterKeyHint: 'done' }}
        />

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#0ea5e9',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.62rem',
            mb: 1,
          }}
        >
          If I fail, I'll…
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {ACCOUNTABILITY_OPTIONS.map((option) => {
            const selected = accountability.includes(option);
            return (
              <Chip
                key={option}
                label={option}
                size="small"
                onClick={() => toggleChip(option)}
                sx={{
                  height: 28,
                  fontSize: '0.73rem',
                  cursor: 'pointer',
                  bgcolor: selected
                    ? 'rgba(14,165,233,0.18)'
                    : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${selected ? 'rgba(14,165,233,0.55)' : 'rgba(255,255,255,0.1)'}`,
                  color: selected ? '#38bdf8' : 'rgba(226,232,240,0.5)',
                  '& .MuiChip-label': { px: 1.25 },
                  '&:hover': { bgcolor: selected ? 'rgba(14,165,233,0.25)' : 'rgba(255,255,255,0.08)' },
                  '&:active': { bgcolor: 'rgba(14,165,233,0.3)' },
                  transition: 'all 0.15s',
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Sign in */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        {challengeText.trim() && (
          <Typography
            variant="caption"
            sx={{ color: 'rgba(226,232,240,0.45)', mb: 0.5 }}
          >
            Sign in to save your challenge
          </Typography>
        )}
        <GoogleLogin
          onSuccess={handleLogin}
          onError={() => {}}
          theme="filled_black"
          size="large"
          shape="rectangular"
          text="signin_with"
        />
      </Box>
    </Box>
  );
}

function AppContent() {
  const { user } = useAuth();
  const [tab, setTab] = useState('challenges');
  const [progressData, setProgressData] = useState({ completed: 0, total: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const showError = useCallback((message) => setSnackbar({ open: true, message, severity: 'error' }), []);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background:
          'linear-gradient(160deg, #0d1526 0%, #0f2044 45%, #0a2d50 100%)',
      }}
    >
      {/* Fixed header */}
      <AppHeader onAvatarClick={() => setTab('profile')} />

      {/* Fixed progress bar — always visible when authenticated, prevents layout shift on tab switch */}
      {user && (
        <ProgressBarStrip
          completed={progressData.completed}
          total={progressData.total}
        />
      )}

      {/* Scrollable content */}
      <Box
        className="scroll-container"
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
        }}
      >
        {!user ? (
          <LoginView />
        ) : tab === 'challenges' ? (
          <Box
            sx={{
              p: 2,
              pt: 2,
              pb: '96px',
              maxWidth: 640,
              width: '100%',
              mx: 'auto',
            }}
          >
            <ChallengesPage
              onProgressUpdate={(c, t) =>
                setProgressData({ completed: c, total: t })
              }
              onError={showError}
            />
          </Box>
        ) : (
          <ProfileView />
        )}
      </Box>

      {/* Fixed footer nav — only shown when authenticated */}
      {user && <AppFooterNav tab={tab} onTabChange={setTab} />}

      {/* PWA install prompt */}
      <InstallPrompt />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: 'calc(48px + env(safe-area-inset-bottom, 0px) + 12px)' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const App = () => (
  <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          html: {
            height: '100%',
            overflow: 'hidden',
            overscrollBehavior: 'none',
          },
          body: {
            height: '100%',
            overflow: 'hidden',
            overscrollBehavior: 'none',
            backgroundColor: '#0d1526',
          },
          '#root': {
            height: '100%',
            overflow: 'hidden',
          },
        }}
      />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  </GoogleOAuthProvider>
);

export default App;
